import { LICENSE_SCOPE, type LicenseScope } from '@saasalerts/common/shared'
import * as Sentry from '@sentry/nextjs'
import type { MeticulousPublicApi } from '@alwaysmeticulous/sdk-bundles-api'
import {
  stopIntercepting,
  tryLoadAndStartRecorder,
} from '@alwaysmeticulous/recorder-loader'

const RECORDING_TOKEN = 'gg6sYjbsp8KQsJYfEzCeph4lK0GofIfB25xD4vgg'

let meticulousReadyPromise: Promise<void> | undefined

const isLicenseScope = (value: unknown): value is LicenseScope =>
  Object.values(LICENSE_SCOPE).some((scope) => scope === value)

export function getMeticulousReplayLicenseScope(): LicenseScope | null {
  if (typeof window === 'undefined') return null

  const meticulous = window.Meticulous
  if (meticulous?.isRunningAsTest !== true) return null

  const replayScope = meticulous.replay.retrieveCustomData('domainScope')
  return isLicenseScope(replayScope) ? replayScope : null
}

export async function initializeMeticulous(): Promise<void> {
  if (typeof window === 'undefined') return

  if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'production') {
    await stopIntercepting()
    return
  }

  try {
    await tryLoadAndStartRecorder({
      recordingToken: RECORDING_TOKEN,
      isProduction: false,
      forceRecording: true,
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

export function waitForMeticulous(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()

  meticulousReadyPromise ??= initializeMeticulous()
  return meticulousReadyPromise
}

export async function withMeticulous(
  callback: (meticulous: MeticulousPublicApi) => void
): Promise<void> {
  await waitForMeticulous()
  if (typeof window === 'undefined' || !window.Meticulous) return

  callback(window.Meticulous)
}

export async function recordMeticulousDomainScope(
  licenseScope: LicenseScope
): Promise<void> {
  await withMeticulous((meticulous) => {
    meticulous.context.recordCustomContext('domainScope', licenseScope)
    if (meticulous.isRunningAsTest !== true && 'record' in meticulous) {
      meticulous.record.recordCustomData('domainScope', licenseScope)
    }
  })
}
